import { useEffect, useState } from "react";
import Plotly from "plotly.js-dist-min";
import createPlotlyComponent from "react-plotly.js/factory";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";
import { buildChartTraces } from "./sensorCountsUtils";
import StructureMilesCard from "./StructureMilesCard";

export { buildChartTraces };

const Plot = createPlotlyComponent(Plotly);


const MILESTONE_MONTHS = [12, 36, 60];
const MILESTONE_LABELS = { 12: "1y", 36: "3y", 60: "5y" };
const X_TICK_VALS = [1, 6, 12, 18, 24, 30, 36, 42, 48, 54, 60];

function buildLayout(title) {
    const shapes = [
        {
            type: "line",
            x0: 0,
            x1: 1,
            xref: "paper",
            y0: 50,
            y1: 50,
            line: { dash: "dash", color: "gray", width: 1 },
        },
        ...MILESTONE_MONTHS.map((m) => ({
            type: "line",
            x0: m,
            x1: m,
            yref: "paper",
            y0: 0,
            y1: 1,
            line: { dash: "dot", color: "#888", width: 1 },
        })),
    ];

    const annotations = [
        ...MILESTONE_MONTHS.map((m) => ({
            x: m,
            y: 1,
            yref: "paper",
            text: MILESTONE_LABELS[m],
            showarrow: false,
            xanchor: "right",
            font: { size: 11, color: "#555" },
        })),
        {
            x: 1,
            xref: "x",
            y: 50,
            yref: "y",
            text: "50%",
            showarrow: false,
            xanchor: "left",
            yanchor: "bottom",
            font: { size: 11, color: "#555" },
        },
    ];

    return {
        hovermode: "closest",
        legend: { title: { text: "Num Sensors" }, x: 1.02, xanchor: "left" },
        xaxis: {
            title: { text: "# Months from Install" },
            tickvals: X_TICK_VALS,
            range: [1, 60],
        },
        yaxis: {
            title: { text: "Probability of Coverage Loss" },
            range: [0, 100],
        },
        shapes,
        annotations,
        title: title ? { text: title, font: { size: 14 } } : undefined,
        margin: { t: title ? 50 : 30, r: 20 },
    };
}

const useStyles = makeStyles(() => ({
    container: {
        display: "flex",
        flexDirection: "row",
        height: "100%",
        gap: "12px",
    },
    chartArea: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
    },
    sidebar: {
        width: "280px",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "8px",
        overflowY: "auto",
    },
    metaRow: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    sidebarField: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    runButton: {
        marginTop: "8px",
    },
    aboutHeader: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        userSelect: "none",
        gap: "4px",
        marginTop: "8px",
    },
    aboutContent: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        paddingTop: "8px",
    },
}));

export default function SensorCountsContent({ client, project, dataset, datasetVersionDttm, sensorPlacementInputData, datasetLoading, onDatasetReload }) {
    const classes = useStyles();
    const [maxMultiplierOption, setMaxMultiplierOption] = useState("2x");
    const [failureRate, setFailureRate] = useState("");
    const [isRunning, setIsRunning] = useState(false);
    const [aboutExpanded, setAboutExpanded] = useState(false);

    useEffect(() => {
        if (dataset?.annual_failure_rate != null) {
            setFailureRate(String(dataset.annual_failure_rate * 100));
        }
    }, [dataset]);

    const MULTIPLIER_OPTIONS = [
        { value: "1.5x", multiplier: 1.5 },
        { value: "2x", multiplier: 2 },
        { value: "2.5x", multiplier: 2.5 },
        { value: "3x", multiplier: 3 },
    ];

    const minSensors = dataset?.initial_sensor_count ?? null;
    const multiplierValue = MULTIPLIER_OPTIONS.find((o) => o.value === maxMultiplierOption)?.multiplier ?? 2;

    const numEligibleStrs = dataset?.num_of_eligible_strs ?? null;
    const maxNumSensors = dataset?.max_num_sensors ?? null;
    const hasEligibleStrs = numEligibleStrs != null;
    const eligibleStrsBelowMax = hasEligibleStrs && maxNumSensors != null && numEligibleStrs < maxNumSensors;

    const parsedFailureRate = parseFloat(failureRate);
    const runValid =
        client &&
        project &&
        failureRate !== "" &&
        !isNaN(parsedFailureRate) &&
        parsedFailureRate > 0 &&
        parsedFailureRate <= 100 &&
        sensorPlacementInputData != null &&
        multiplierValue != null;

    const handleRunAnalysis = async () => {
        setIsRunning(true);
        try {
            await calibrationService.triggerAspenInputPipeline(
                client,
                project,
                { phases: [2], annual_failure_rate: parsedFailureRate / 100, n_sensor_multiplier: multiplierValue},
                [{ dataset_type: "sensor-placement-input", group_key: DEFAULT_GROUP_KEY, version_dttm: sensorPlacementInputData?.meta?.version_dttm }]
            );
        } finally {
            setIsRunning(false);
        }
    };

    const traces = buildChartTraces(dataset);
    const plotTitle = dataset?.annual_failure_rate != null
        ? `Probability of Coverage Loss w/ ${Math.round(dataset.annual_failure_rate * 100)}% Annual Failure Rate<br>${client} / ${project}`
        : undefined;
    const layout = buildLayout(plotTitle);

    return (
        <div className={classes.container}>
            <div className={classes.chartArea}>
                {datasetLoading ? (
                    <CircularProgress />
                ) : dataset ? (
                    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
                        {datasetVersionDttm && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography variant="caption" style={{ fontStyle: "italic" }}>
                                    Displaying sensor count analysis from {new Date(datasetVersionDttm).toLocaleString()}
                                </Typography>
                                <IconButton size="small" onClick={onDatasetReload}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </div>
                        )}
                        <Plot
                            data={traces}
                            layout={layout}
                            style={{ width: "100%", flex: 1 }}
                            useResizeHandler
                            config={{
                                responsive: true,
                                toImageButtonOptions: {
                                    filename: `${client}_${project}_coverage_loss_probability_failure_rate_${String(dataset.annual_failure_rate ?? "").replace(".", "_")}`,
                                },
                            }}
                        />
                    </div>
                ) : (
                    <Typography color="textSecondary">
                        {client && project ? "Please Build Analysis Inputs" : "Select a client and project to load data."}
                    </Typography>
                )}
            </div>
            <div className={classes.sidebar}>
                <div className={classes.sidebarField}>
                    <div className={classes.metaRow}>
                        <Typography variant="body2" style={{ fontWeight: "bold" }}>
                            Minimum # of Sensors
                        </Typography>
                        <Tooltip title="Calculated as 1 sensor every 7km, and ensuring 1 sensor per line (double-circuits can share 1 sensor).">
                            <InfoOutlinedIcon fontSize="small" style={{ color: "#888", cursor: "help" }} />
                        </Tooltip>
                    </div>
                    <Typography variant="body1">{minSensors ?? "—"}</Typography>
                </div>

                <div className={classes.sidebarField}>
                    <div className={classes.metaRow}>
                        <Typography variant="body2" style={{ fontWeight: "bold" }}>
                            # of Eligible Structures
                        </Typography>
                        <Tooltip title="If the number of eligible structures in this project are less than total range of sensors determined by the multiplier, the graph will only be populated with lines up until the maximum number of eligible structures.">
                            <InfoOutlinedIcon fontSize="small" style={{ color: "#888", cursor: "help" }} />
                        </Tooltip>
                    </div>
                    {hasEligibleStrs ? (
                        <Typography variant="body1" style={{ color: eligibleStrsBelowMax ? "orange" : undefined }}>
                            {numEligibleStrs}
                        </Typography>
                    ) : (
                        <Typography variant="body1" color="textSecondary">
                            N/A
                        </Typography>
                    )}
                </div>

                <div className={classes.sidebarField}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Base Sensor Count Multiplier</InputLabel>
                        <Select
                            value={maxMultiplierOption}
                            label="Base Sensor Count Multiplier"
                            onChange={(e) => setMaxMultiplierOption(e.target.value)}
                        >
                            {MULTIPLIER_OPTIONS.map(({ value }) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <TextField
                    label="Annual Failure Rate (%)"
                    type="number"
                    size="small"
                    value={failureRate}
                    onChange={(e) => setFailureRate(e.target.value)}
                    inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                    fullWidth
                />

                {client && project && !datasetLoading && (!sensorPlacementInputData || !dataset) && (
                    <Typography variant="body2" color="textSecondary">
                        Missing datasets:{" "}
                        {[
                            !sensorPlacementInputData && "Sensor Placement Input",
                            !dataset && "Sensor Count Analysis",
                        ]
                            .filter(Boolean)
                            .join(", ")}
                    </Typography>
                )}
                <Button
                    className={classes.runButton}
                    variant="contained"
                    color="primary"
                    disabled={!runValid || isRunning}
                    onClick={handleRunAnalysis}
                    fullWidth
                >
                    {isRunning ? "Running..." : "Run Analysis"}
                </Button>
                <StructureMilesCard client={client} project={project} />
                <div className={classes.aboutHeader} onClick={() => setAboutExpanded((prev) => !prev)}>
                    {aboutExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                        About this Coverage Loss Simulation Tool
                    </Typography>
                </div>
                <Collapse in={aboutExpanded}>
                    <div className={classes.aboutContent}>
                        <Typography variant="body2">
                            This tool simulates how sensor failure affects the span coverage required for the lowest
                            uncertainty DLR. Per LineRate requirements, spans must be within 20km of at least 2 sensors
                            to receive this uncertainty.
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: "bold", marginTop: "8px" }}>
                            Model
                        </Typography>
                        <Typography variant="body2">
                            In each scenario for a sensor count, every sensor gets a random time to failure over a 5
                            year period, given the annual failure rate (constant hazard model). Each scenario is run 10k
                            times (monte carlo) to generate the probability that coverage is lost at each month from
                            install.
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: "bold", marginTop: "8px" }}>
                            Sensor Count
                        </Typography>
                        <Typography variant="body2">
                            Given a set of line paths, a minimum sensor count is calculated based on LineRates&apos;
                            guidelines - 1 sensor every 7km and at least 1 sensor per line. If
                            lines are double circuit, 1 sensor can cover both.
                        </Typography>
                        <Typography variant="body2">
                            The base sensor count multiplier should be specified in this tool - it&apos;s been preset with 1.5x,
                            2x, 2.5x, and 3x the minimum sensor count.
                        </Typography>
                    </div>
                </Collapse>
            </div>
        </div>
    );
}
